import { useMediaQuery, useDebouncedValue } from "@mantine/hooks";
import {
    Button,
    Input,
    Modal,
    NumberInput,
    Tabs,
    Badge,
    Pagination
} from "@mantine/core";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    IconCheck,
    IconEdit,
    IconSearch
} from "@tabler/icons-react";
import moment from "moment";
import Navigation from "../../components/customer/sidebar/Navigation";
import { createB2BAPI } from "../../api/Interceptor";
import B2BTableGrid from "../../components/common/B2BTableGrid";
import notify from "../../utils/Notification";
import { DateInput } from "@mantine/dates";
import "@mantine/dates/styles.css";
import { useLoader } from "../../Context/LoaderContext";
import "./VendorStockAvailability.css";

export default function VendorStockAvailability() {

    const isMobile = useMediaQuery("(max-width: 767px)");
    const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 991px)");

    const navigate = useNavigate();
    const { showLoader, hideLoader } = useLoader();

    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.userId;
    const userRole = user?.role;

    const B2B_API = useMemo(() => createB2BAPI(navigate), [navigate]);

    const [activeTab, setActiveTab] = useState("pending");

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [rowCount, setRowCount] = useState(0);

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 5
    });

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch] = useDebouncedValue(searchTerm, 500);

    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(new Date());

    const [editModalOpened, setEditModalOpened] = useState(false);

    const [selectedRow, setSelectedRow] = useState(null);

    const [availableRolls, setAvailableRolls] = useState(0);
    const [availableQuantity, setAvailableQuantity] = useState(0);
    const [availableQtyPerRoll, setAvailableQtyPerRoll] = useState(0);
    const [confirmModalOpened, setConfirmModalOpened] = useState(false);
    const [confirmRejectModalOpened, setConfirmRejectModalOpened] = useState(false);
    const [approveId, setApproveId] = useState(null);
    const [rejectId, setRejectId] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const fetchData = async () => {
        setIsLoading(true);
        showLoader();

        try {

            const formattedFromDate = fromDate
                ? moment(fromDate).format("DD/MM/YYYY")
                : "";

            const formattedToDate = toDate
                ? moment(toDate).format("DD/MM/YYYY")
                : "";

            const response = await B2B_API
                .get(
                    `vendorStockAvailability?vendorId=${userId}&vsStatus=${activeTab === "approved" ? "APPROVED" : activeTab === "rejected" ? "REJECTED" : "PENDING"}&search=${debouncedSearch}&fromDate=${formattedFromDate}&toDate=${formattedToDate}&page=${pagination.pageIndex}&size=${pagination.pageSize}`
                )
                .json();

            const fetchedData =
                response?.response?.content ||
                response?.response ||
                [];

            setData(fetchedData);

            setRowCount(
                response?.response?.totalElements ||
                fetchedData.length ||
                0
            );

        } catch (error) {

            console.error(error);

            notify({
                error: true,
                success: false,
                title: "Error",
                message: "Unable to fetch stock availability"
            });

        } finally {
            setIsLoading(false);
            hideLoader();
        }
    };

    useEffect(() => {
        fetchData();
    }, [
        activeTab,
        pagination.pageIndex,
        pagination.pageSize,
        debouncedSearch,
        fromDate,
        toDate
    ]);
    const openApproveConfirm = (id) => {
        setApproveId(id);
        setEditModalOpened(false);
        setConfirmModalOpened(true);
    };
    const openRejectConfirm = (id) => {
        setRejectId(id);
        setEditModalOpened(false);
        setConfirmRejectModalOpened(true);
    };
    const handleEdit = (row) => {

        setSelectedRow(row);

        setAvailableRolls(row?.availableRolls || 0);
        setAvailableQuantity(row?.availableQuantity || 0);
        setAvailableQtyPerRoll(row?.availableQtyPerRoll || 0);

        setEditModalOpened(true);
    };
    useEffect(() => {

        const totalQty =
            Number(availableRolls || 0) *
            Number(availableQtyPerRoll || 0);

        setAvailableQuantity(totalQty);

    }, [availableRolls, availableQtyPerRoll]);
    const handleUpdate = async () => {

        setIsUpdating(true);

        try {
            const body = {
                ...selectedRow,

                availableRolls: availableRolls,

                availableQtyPerRoll: availableQtyPerRoll,

                availableQuantity:
                    Number(availableRolls || 0) *
                    Number(availableQtyPerRoll || 0),

                isUpdated: true
            };

            await B2B_API
                .post("vendorStockAvailability/update", {
                    json: body
                })
                .json();

            notify({
                success: true,
                error: false,
                title: "Updated Successfully",
                message: "Stock availability updated"
            });

            setEditModalOpened(false);

            fetchData();

        } catch (error) {

            console.error(error);

            notify({
                success: false,
                error: true,
                title: "Update Failed",
                message: error?.message || "Something went wrong"
            });

        } finally {
            setIsUpdating(false);
        }
    };
    const isUpdateDisabled =
        !availableRolls ||
        !availableQtyPerRoll ||
        Number(availableRolls) <= 0 ||
        Number(availableQtyPerRoll) <= 0;
    const handleApprove = async (id) => {
        try {
            await B2B_API
                .post(`vendorStockAvailability/approve?id=${id}`)
                .json();
            notify({
                success: true,
                error: false,
                title: "Approved",
                message: "Stock approved successfully"
            });
            setConfirmModalOpened(false);
            setApproveId(null);
            setActiveTab("approved");
            fetchData();
        } catch (error) {
            console.error(error);

            notify({
                success: false,
                error: true,
                title: "Approval Failed",
                message: "Unable to approve"
            });
        }
    };
    const handleReject = async (id) => {
        try {
            await B2B_API
                .post(`vendorStockAvailability/reject?id=${id}`)
                .json();
            notify({
                success: true,
                error: false,
                title: "Rejected",
                message: "Stock rejected successfully"
            });
            setConfirmRejectModalOpened(false);
            setRejectId(null);
            setActiveTab("rejected");
            fetchData();
        } catch (error) {
            console.error(error);

            notify({
                success: false,
                error: true,
                title: "Rejection Failed",
                message: "Unable to reject"
            });
        }
    };
    const hasUpdatedRows = data?.some(row => row.isUpdated);
    const columns = useMemo(() => {
        const baseColumns = [
            {
                header: "Date",
                accessorKey: "createdDate",
                accessorFn: row => moment(row?.createdDate).format("DD/MM/YYYY"),
                size: 180
            },
            {
                header: "Product",
                accessorKey: "productName",
                size: 180
            },
            {
                header: "Variant",
                accessorKey: "variantName",
                size: 180
            },
            {
                header: "SKU",
                accessorKey: "variantSku",
                size: 180
            },
            {
                header: "Required Rolls",
                accessorKey: "requiredRolls",
                size: 180
            },
            {
                header: "Required Qty",
                accessorKey: "requiredQuantity",
                accessorFn: row => `${row?.requiredQuantity || 0}`,
                size: 180
            },
            {
                header: "Available Rolls",
                accessorKey: "availableRolls",
                accessorFn: row => row?.availableRolls || 0,
                size: 180
            },
            {
                header: "Available Qty",
                accessorKey: "availableQuantity",
                accessorFn: row => row?.availableQuantity || 0,
                size: 180
            }
        ];

        if (activeTab === "approved" || activeTab === "rejected") {
            return [
                ...baseColumns,
                {
                    header: "Status",
                    accessorKey: "vsStatus",
                    size: 180,
                    cell: () => (
                        <Badge color={activeTab === "approved" ? "green" : "red"} variant="light" size="sm" radius="sm">
                            {activeTab === "approved" ? "Approved" : "Rejected"}
                        </Badge>
                    )
                }
            ];
        }

        const pendingColumns = [
            ...baseColumns,
            {
                header: "Status",
                accessorKey: "isUpdated",
                size: 180,
                cell: ({ row }) => (
                    <Badge
                        color={row.original.isUpdated ? "green" : "orange"}
                        variant="light" size="sm" radius="sm"
                    >
                        {row.original.isUpdated ? "Updated" : "Pending"}
                    </Badge>
                )
            },
            {
                header: "Action",
                accessorKey: "action",
                size: 140,
                cell: ({ row }) => (
                    <div
                        style={{
                            display: "flex",
                            justifyContent:"center",
                            gap: "6px",
                            cursor: "pointer",
                            fontWeight: 600
                        }}
                        onClick={() => handleEdit(row.original)}
                    >
                        <IconEdit size={18} />
                    </div>
                )
            }
        ];
        return pendingColumns;
    }, [activeTab, data]);
    const totalPages = Math.ceil(rowCount / pagination.pageSize);

    const renderMobileCards = () => {
        if (isLoading) return <div style={{ textAlign: "center", padding: "2rem" }}>Loading...</div>;
        if (!data || data.length === 0) return <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>No data found.</div>;

        return (
            <div className="vsa-mobile-cards">
                {data.map((item, index) => (
                    <div key={index} className="vsa-card">
                        <div className="vsa-card-header">
                            <span className="vsa-card-date">{moment(item?.createdDate).format("DD/MM/YYYY")}</span>
                            {activeTab === "approved" || activeTab === "rejected" ? (
                                <Badge color={activeTab === "approved" ? "green" : "red"} variant="light" size="sm" radius="sm">
                                    {activeTab === "approved" ? "Approved" : "Rejected"}
                                </Badge>
                            ) : (
                                <Badge color={item.isUpdated ? "green" : "orange"} variant="light" size="sm" radius="sm">
                                    {item.isUpdated ? "Updated" : "Pending"}
                                </Badge>
                            )}
                        </div>
                        <div className="vsa-card-body">
                            <div className="vsa-card-row">
                                <span className="vsa-card-label">Product:</span>
                                <span className="vsa-card-value">{item.productName}</span>
                            </div>
                            <div className="vsa-card-row">
                                <span className="vsa-card-label">Variant:</span>
                                <span className="vsa-card-value">{item.variantName}</span>
                            </div>
                            <div className="vsa-card-row">
                                <span className="vsa-card-label">SKU:</span>
                                <span className="vsa-card-value">{item.variantSku}</span>
                            </div>
                            <div className="vsa-card-row">
                                <span className="vsa-card-label">Required Qty:</span>
                                <span className="vsa-card-value">{item.requiredRolls} Rolls / {item.requiredQuantity}</span>
                            </div>
                            <div className="vsa-card-row">
                                <span className="vsa-card-label">Available Qty:</span>
                                <span className="vsa-card-value">{item.availableRolls || 0} Rolls / {item.availableQuantity || 0}</span>
                            </div>
                        </div>
                        {activeTab === "pending" && (
                            <div className="vsa-card-actions">
                                <Button
                                    size="xs"
                                    variant="light"
                                    color="#8BC53F"
                                    leftSection={<IconEdit size={14} />}
                                    onClick={() => handleEdit(item)}
                                >
                                    Edit
                                </Button>
                            </div>
                        )}
                    </div>
                ))}
                {totalPages > 1 && (
                    <div className="vsa-pagination-mobile">
                        <Pagination
                            total={totalPages}
                            value={pagination.pageIndex + 1}
                            onChange={(page) => setPagination(p => ({ ...p, pageIndex: page - 1 }))}
                            color="#8BC53F"
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="view-my-page router-view-container">

            <div className="route-view">

                {!isMobile && !isTablet && <Navigation />}

                <div className="vsa-container">

                    <h2 className="vsa-title">
                        Vendor Stock Availability
                    </h2>

                    <Tabs
                        value={activeTab}
                        onChange={setActiveTab}
                        color="#8BC53F"
                    >

                        <Tabs.List mb="lg">

                            <Tabs.Tab value="pending">
                                Pending
                            </Tabs.Tab>

                            <Tabs.Tab value="approved">
                                Approved
                            </Tabs.Tab>

                            <Tabs.Tab value="rejected">
                                Rejected
                            </Tabs.Tab>

                        </Tabs.List>

                    </Tabs>

                    <div className="vsa-filters">

                        <Input
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) =>
                                setSearchTerm(e.currentTarget.value)
                            }
                            rightSection={
                                <IconSearch size={16} />
                            }
                        />

                        <DateInput
                            placeholder="From date"
                            value={fromDate}
                            onChange={setFromDate}
                            clearable
                            maxDate={toDate || new Date()}
                        />

                        <DateInput
                            placeholder="To date"
                            value={toDate}
                            onChange={setToDate}
                            clearable
                            minDate={fromDate}
                            maxDate={new Date()}
                        />

                        <Button 
                             color="red"
                            variant="light"
                            disabled={!searchTerm && !fromDate}
                            onClick={() => {
                                setSearchTerm("");
                                setFromDate(null);
                                setToDate(new Date());
                            }}
                        >
                            Clear
                        </Button>
                    </div>

                    {isMobile ? (
                        renderMobileCards()
                    ) : (
                        <B2BTableGrid
                            columns={columns}
                            data={data}
                            isLoading={isLoading}
                            rowCount={rowCount}
                            pagination={pagination}
                            setPagination={setPagination}
                            onPaginationChange={setPagination}
                            manualPagination={true}
                        />
                    )}

                </div>

            </div>

            <Modal
                opened={editModalOpened}
                onClose={() => setEditModalOpened(false)}
                centered
                title="Update Stock Availability"
                size="md"
            >

                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem"
                    }}
                >

                    <div style={{ fontSize: "14px" }}>

                        <strong>Product :</strong>
                        {" "}
                        {selectedRow?.productName}

                        <br />

                        <strong>Variant :</strong>
                        {" "}
                        {selectedRow?.variantName}

                    </div>


                    <NumberInput
                        label="Required Rolls"
                        value={selectedRow?.requiredRolls || 0}
                        disabled
                    />

                    <NumberInput
                        label="Required Qty"
                        value={selectedRow?.requiredQuantity || 0}
                        disabled
                    />
                    <NumberInput
                        label="Required Qty / Roll"
                        value={selectedRow?.requiredQtyPerRoll || 0}
                        disabled
                    />
                    <Input.Wrapper label="Available Rolls">
                        <Input
                            type="number"
                            value={availableRolls}
                            onChange={(e) => {
                                const maxAllowed = Math.min(selectedRow?.requiredRolls || 0, 999);

                                let num = Number(e.currentTarget.value || 0);

                                if (num > maxAllowed) num = maxAllowed;

                                setAvailableRolls(num);
                            }}
                        />
                    </Input.Wrapper>
                    <Input.Wrapper label="Available Qty / Roll">
                        <Input
                            type="number"
                            value={availableQtyPerRoll}
                            onChange={(e) =>
                                setAvailableQtyPerRoll(Number(e.currentTarget.value || 0))
                            }
                        />
                    </Input.Wrapper>

                    <NumberInput
                        label="Available Quantity"
                        value={availableQuantity}
                        disabled
                    />

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexWrap: "nowrap",
                            gap: "6px",
                            marginTop: "1rem",
                            width: "100%"
                        }}
                    >
                        <Button
                            size="xs"
                            style={{ flex: 1, padding: "0 4px", fontSize: "12px" }}
                            color="#ff7b00"
                            loading={isUpdating}
                            onClick={handleUpdate}
                            disabled={isUpdateDisabled}
                        >
                            Update
                        </Button>
                        {selectedRow?.isUpdated && (
                            <>
                                <Button
                                    size="xs"
                                    style={{ flex: 1, padding: "0 4px", fontSize: "12px" }}
                                    color="#8BC53F"
                                    onClick={() => openApproveConfirm(selectedRow.id)}
                                >
                                    Approve
                                </Button>
                            </>
                        )}
                        <Button
                            size="xs"
                            style={{ flex: 1, padding: "0 4px", fontSize: "12px" }}
                            color="red"
                            onClick={() => openRejectConfirm(selectedRow.id)}
                        >
                            Reject
                        </Button>
                    </div>

                </div>

            </Modal>
            <Modal
                opened={confirmModalOpened}
                onClose={() => setConfirmModalOpened(false)}
                centered
                title="Confirm Approval"
            >
                <div>Are you sure you want to approve this item?</div>

                <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    marginTop: "15px"
                }}>
                    <Button
                        color="red"
                        onClick={() => setConfirmModalOpened(false)}
                    >
                        Cancel
                    </Button>

                    <Button
                        color="#8BC53F"
                        onClick={() => handleApprove(approveId)}
                    >
                        Confirm
                    </Button>
                </div>
            </Modal>
            <Modal
                opened={confirmRejectModalOpened}
                onClose={() => setConfirmRejectModalOpened(false)}
                centered
                title="Confirm Rejection"
            >
                <div>Are you sure you want to reject this item?</div>

                <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    marginTop: "15px"
                }}>
                    <Button
                        variant="outline"
                        color="gray"
                        onClick={() => setConfirmRejectModalOpened(false)}
                    >
                        Cancel
                    </Button>

                    <Button
                        color="red"
                        onClick={() => handleReject(rejectId)}
                    >
                        Reject
                    </Button>
                </div>
            </Modal>
        </div >
    );
}